import React from "react";
import {
  AspectRatio,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Image,
  Input,
  Stack,
  Text,
  Textarea,
  useColorMode,
  useDisclosure,
} from "@chakra-ui/react";
import LandingFooter from "@/src/components/LandingFooter";
import { Icon } from "@chakra-ui/icons";
import { MdMail } from "react-icons/md";
import { FaLinkedin, FaTwitter, FaYoutube } from "react-icons/fa";
import { useForm } from "react-hook-form";
import LandingHeader from "@/src/components/LandingHeader";
import ContactSuccessModal from "@/src/components/ContactSuccessModal";
import LandingShell from "@/src/layouts/LandingShell";
import useTranslation from "next-translate/useTranslation";
import TopBackground from "@/src/components/TopBackground";
import { validateGeneralEmail as validateEmail } from "@/src/utils/validate-functions";
import { contact } from "@/services/contact";

const ContactShell = () => {
  return (
    <LandingShell>
      <LandingHeader />
      <TopBackground />
      <ContactContain />
      <LandingFooter />
    </LandingShell>
  );
};

const ContactContain = () => {
  const { t } = useTranslation();
  const { onOpen, isOpen, onClose } = useDisclosure();
  const [loading, setLoading] = React.useState(false);
  const [isSend, setIsSend] = React.useState(false);
  const [success, setSuccess] = React.useState("");
  const [error, setError] = React.useState("");

  const { colorMode } = useColorMode();

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset
  } = useForm({ mode: "all" });

  async function onSubmit(data) {
    setLoading(true);
    const resp = await contact(data);
    if (resp?.contact?.contact?.status === "ok") {
      setLoading(false);
      setIsSend(true);
      setSuccess(t("contact:modal.message"));
      onOpen();
      reset();
    } else {
      setIsSend(false);
      setLoading(false);
      setError(t("contact:modal.error_message"));
      setLoading(false);
    }
  }

  return (
    <Stack
      as={"section"}
      direction={"column"}
      py={"80px"}
      alignItems={"center"}
      px={{ base: "10px", lg: "120px" }}
    >
      <Stack position={"relative"}>
        <Image
          alt={"dot-top"}
          src={"/images/dot-top.svg"}
          right={"-65px"}
          position={"absolute"}
          top={"-68px"}
        />
        <Stack
          zIndex={5}
          bg={colorMode === "light" ? "white" : "rgba(0, 178, 149, 0.33)"}
          maxW={"950px"}
          shadow={"md"}
          borderRadius={"10px"}
          py={"60px"}
          px={{ base: "0px", md: "30px", lg: "60px" }}
        >
          <Text
            fontSize={"24px"}
            textAlign={"center"}
            color={"brand.500"}
            fontWeight={700}
          >
            {t("contact:title")}
          </Text>
          <Flex
            direction={{ base: "column", md: "row" }}
            padding={8}
            justifyContent={"center"}
          >
            <Flex direction={"column"} mb={{ base: 12, md: 0 }}>
              <Text
                color={colorMode === "light" ? "inherit" : "white"}
                fontSize={"20px"}
                lineHeight={"30px"}
                fontWeight={500}
              >
                {t("contact:subtitle")}
              </Text>
              <Stack
                as={"form"}
                onSubmit={handleSubmit((data) => onSubmit(data))}
                pt={4}
                spacing={"14px"}
                h={"full"}
                maxH={"450px"}
              >
                <FormControl isInvalid={errors.name}>
                  <Input
                     borderColor={
                      colorMode === "light" ? "inherit" : "brand.700"
                    }
                    bg={colorMode === "light" ? "white" : "transparent"}
                    color={colorMode === 'light' ? 'brand.500' : 'white'}
                    _placeholder={{
                      color: colorMode === "light" ? "inherit" : "gray.200",
                    }}
                    _hover={{
                      bgColor:
                        colorMode === "light" ? "white" : "transparent",
                    }}
                    _focus={{
                      bgColor:
                        colorMode === "light" ? "white" : "transparent",
                    }}
                    height={"50px"}
                    placeholder={t("contact:name.placeholder")}
                    type={"text"}
                    {...register("name", {
                      required: t("contact:name.required"),
                    })}
                  />
                  <FormErrorMessage>
                    {errors?.name && errors?.name.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={errors?.email}>
                  <Input
                     borderColor={
                      colorMode === "light" ? "inherit" : "brand.700"
                    }
                    bg={colorMode === "light" ? "white" : "transparent"}
                    color={colorMode === 'light' ? 'brand.500' : 'white'}
                    _placeholder={{
                      color: colorMode === "light" ? "inherit" : "gray.200",
                    }}
                    _hover={{
                      bgColor:
                        colorMode === "light" ? "white" : "transparent",
                    }}
                    _focus={{
                      bgColor:
                        colorMode === "light" ? "white" : "transparent",
                    }}
                    height={"50px"}
                    placeholder={t("contact:email.placeholder")}
                    type={"email"}
                    {...register("email", {
                      required: t("contact:email.required"),
                      validate: (email) => {
                        return validateEmail(email, t);
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {errors?.email && errors?.email.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={errors?.message}>
                  <Textarea
                     borderColor={
                      colorMode === "light" ? "inherit" : "brand.700"
                    }
                    bg={colorMode === "light" ? "white" : "transparent"}
                    color={colorMode === 'light' ? 'brand.500' : 'white'}
                    _placeholder={{
                      color: colorMode === "light" ? "inherit" : "gray.200",
                    }}
                    _hover={{
                      bgColor:
                        colorMode === "light" ? "white" : "transparent",
                    }}
                    _focus={{
                      bgColor:
                        colorMode === "light" ? "white" : "transparent",
                    }}
                    colorScheme={"brand"}
                    placeholder={t("contact:text.placeholder")}
                    h={"137px"}
                    resize={"none"}
                    {...register("message", {
                      required: t("contact:text.required"),
                    })}
                  />
                  <FormErrorMessage>
                    {errors?.message && errors?.message?.message}
                  </FormErrorMessage>
                </FormControl>
                <Stack>
                  <Button
                    type={"submit"}
                    isLoading={loading}
                    colorScheme={"brand"}
                  >
                    {t("contact:button")}
                  </Button>
                </Stack>
                {isSend && (
                  <ContactSuccessModal
                    success={success}
                    error={error}
                    onClose={onClose}
                    isOpen={isOpen}
                  />
                )}
              </Stack>
            </Flex>
            <Flex
              direction={"column"}
              justify={"center"}
              flex={"0 1 35%"}
              ml={{ base: 0, md: 20 }}
              maxH={"420px"}
            >
              <Text color={colorMode === "light" ? "inherit" : "white"}>
                {t("contact:address")}
              </Text>
              <Text color={colorMode === "light" ? "inherit" : "white"} pt={4}>
                +229 21 60 42 52/ 91 91 15 91
              </Text>
              <Flex pt={4} gap={2} align={"center"}>
                <Icon
                  color={colorMode === "light" ? "inherit" : "white"}
                  as={MdMail}
                />
                <Text color={colorMode === "light" ? "inherit" : "white"}>
                  support@watchman.bj
                </Text>
              </Flex>
              <Flex pt={4} align={"center"}>
                <Icon
                  color={colorMode === "light" ? "inherit" : "white"}
                  mr={4}
                  as={FaYoutube}
                />
                <Icon
                  color={colorMode === "light" ? "inherit" : "white"}
                  mr={4}
                  as={FaLinkedin}
                />
                <Icon
                  color={colorMode === "light" ? "inherit" : "white"}
                  as={FaTwitter}
                />
              </Flex>
              <AspectRatio ratio={12 / 6} mt={4}>
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4782.808884532941!2d2.481077496446415!3d6.3673738067783345!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x103cab7931fb81ef%3A0x649924aef9c6f000!2sGlobal%20It%20Service!5e0!3m2!1sen!2sng!4v1699954624553!5m2!1sen!2sng" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
              </AspectRatio>
            </Flex>
          </Flex>
        </Stack>
        <Image
          alt={"dot-bottom"}
          src={"/images/dot-top.svg"}
          bottom={"-76px"}
          left={"-71px"}
          position={"absolute"}
        />
      </Stack>
    </Stack>
  );
};

export default ContactShell;
